import $ from 'jquery';
import { BehaviorSubject, fromEvent, Subject, merge } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';

import { Component, ElementRef, OnInit } from '@angular/core';

import { FlashMessage } from '@services/flash-message';

export interface IFlashMessageOptions {
  /** Secondary text, if required. */
  secondaryMessage?: string;
  /** 'error', 'warning', 'success' */
  type?: string;
  /** If true, the message should hide automatically */
  autoHide?: boolean;
  /** Time until the message hides, if autoHide is active. */
  hideFlashTime?: number;
  /** Whether to center the message. */
  centerAlignMessage?: boolean;
  /** A callback to be called when the message is completed or interrupted and hidden. */
  hideCallBack?: () => void;
}

export const HIDE_FLASH_TIME = 7000;

@Component({
  selector: 'flash-message',
  templateUrl: './flash-message.component.html',
  styleUrls: ['./flash-message.component.scss'],
})
export class FlashMessageComponent implements OnInit {
  private $el;
  private $message;

  public message: string;
  public secondaryMessage: string;
  public type: string;
  public centerAlignMessage: boolean = false;

  private lastAddedHidePromise: Promise<void> = Promise.resolve();
  private click$: Subject<void> = new Subject();
  private skip$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    el: ElementRef,
    flashMessage: FlashMessage,
  ) {
    this.$el = $(el.nativeElement);
    flashMessage.registerComponent(this);
  }

  public ngOnInit() {
    this.$message = this.$el.find('.ui.message');
    fromEvent($(this.$message), 'click')
      .pipe(debounceTime(200))
      .subscribe(() => this.click$.next());
  }

  /**
   * Put a message into the flash message queue.
   * @param message The main message to display.
   * @param options
   * @returns A promise to be resolved when the message is completed or interrupted and hidden.
   */
  public async show(message: string, {
    secondaryMessage = null,
    type = '',
    autoHide = true,
    centerAlignMessage = false,
    hideFlashTime = HIDE_FLASH_TIME,
    hideCallBack = () => null,
  }: IFlashMessageOptions = {},
  ): Promise<void> {
    const lastAddedHidePromise: Promise<void> = this.lastAddedHidePromise;
    this.lastAddedHidePromise = new Promise<void>(async (thisHidePromiseResolver: () => void) => {
      // Wait for previous message to complete.
      await lastAddedHidePromise;

      if (!this.skip$.getValue()) {
        // Update message data.
        this.message = message;
        this.secondaryMessage = secondaryMessage;
        this.type = type;
        this.centerAlignMessage = centerAlignMessage;

        // Display message.
        await this.flyDown();
        // Pause on message.
        await new Promise<void>(resolve => {
          merge(
            this.click$,
            this.skip$.pipe(filter(skip => skip)),
          )
            .pipe(take(1))
            .subscribe(() => resolve());
          if (autoHide) setTimeout(resolve, hideFlashTime);
        });
        // Hide message.
        await this.flyDown();
      }
      hideCallBack();
      thisHidePromiseResolver();
    });
    return this.lastAddedHidePromise;
  }

  /**
   * Interrupt current message and skip all subsequent messages.
   * @returns A promise indicating that all messages have been finished.
   */
  public async clear(): Promise<void> {
    this.skip$.next(true);
    await this.lastAddedHidePromise;
    this.skip$.next(false);
  }

  /**
   * Show or hide message component (toggle visibility).
   */
  private async flyDown(): Promise<void> {
    return new Promise<void>(resolve => {
      this.$message.transition('fly down', { onComplete: resolve, silent: true });
    });
  }
}
